import { googlePayApprove, innerSearch } from 'api/Api';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import PayverseHeader from 'app/components/PayverseHeader';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import GooglePayButton from '@google-pay/button-react';
import { API_HOST } from 'api/ApiInstance';

const GooglePayScreen = () => {
  const searchParams = useLocation().search;
  const parsedParams = new URLSearchParams(searchParams);

  const paramTid = parsedParams.get('tid');
  const paramAmount = parsedParams.get('approvalAmount');
  const paramCurrency = parsedParams.get('approvalCurrency');
  const paramCountry = parsedParams.get('countryCode');
  const paramDescriptor = parsedParams.get('descriptor');
  const paramCardBrandList = parsedParams.get('cardBrandList');
  const paramMerchantCapabilities = parsedParams.get('merchantCapabilities');
  // const paramProtocolVersion = parsedParams.get('protocolVersion');
  // const paramPublicKey = parsedParams.get('publicKey');
  const paramMerchantId = parsedParams.get('merchantId');
  const paramMerchantName = parsedParams.get('merchantName');
  const paramTotalPriceLabel = parsedParams.get('totalPriceLabel') ?? 'Total';
  const paramTotalPriceStatus = (parsedParams.get('totalPriceStatus') ??
    'FINAL') as google.payments.api.TotalPriceStatus;

  const { setPaymentState, setShopifyState } = usePaymentState();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  /**
   * appScheme:
   * 결제사 앱 종료 후 원래 사용하던 앱으로 복귀시키기 위한 scheme
   */
  const [appScheme, setAppScheme] = useState<string>('');
  const [isApproveProcessing, setIsApproveProcessing] = useState(false);

  const parseBarSeparatedString = <T extends string>(
    value: string | null,
  ): T[] => {
    if (!value) return [];
    return value.toUpperCase().split('|').filter(Boolean) as T[];
  };

  const allowedCardNetworks =
    parseBarSeparatedString<google.payments.api.CardNetwork>(
      paramCardBrandList,
    );
  const allowedAuthMethods =
    parseBarSeparatedString<google.payments.api.CardAuthMethod>(
      paramMerchantCapabilities,
    );

  const updatePaymentState = infoData => {
    setPaymentState(prevState => ({
      ...prevState,
      requestCurrency: infoData.display.requestCurrency,
      requestAmount: infoData.display.requestAmount,
      reqDate: infoData.display.responseDatetime,
      orderId: infoData.response.orderId,
      resultCode: infoData.response.resultCode,
      resultMessage: infoData.response.resultMessage,
      mallReserved: infoData.response.mallReserved,
      billKey: infoData.response.billKey,
      schemeGroup: infoData.response.schemeGroup,
      schemeCode: infoData.response.schemeCode,
    }));
  };

  const transactStateSet = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      updatePaymentState(data.data);
    },
    onError: () => {
      navigate('/error');
    },
  });

  const transactResult = useMutation(() => innerSearch(paramTid), {
    onSuccess: data => {
      if (data.data.returnUrl.includes('shopifyConfirm')) {
        setShopifyState(data.data.response);
        navigate('/shopifyConfirm');
      } else {
        payResult(data);
        if (
          data.data.response.resultStatus === null ||
          data.data.response.resultStatus === undefined
        ) {
          navigate('/error');
        }
      }
    },
    onError: () => {
      navigate('/error');
    },
  });

  const payResult = data => {
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      billKey: data.data.response.billKey,
      returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  useEffect(() => {
    if (paramTid) {
      setPaymentState(prevState => ({ ...prevState, tid: paramTid }));
      transactStateSet.mutate();
    }
  }, [paramTid]);

  useEffect(() => {
    const storedLang = localStorage.getItem('lang');
    if (storedLang) {
      i18n.changeLanguage(storedLang);
    }
  }, []);

  /**
   * needAuth === 'Y' 인 경우 3DS 인증 페이지로 form POST 이동
   */

  const submitThirdPartyAuthForm = (tid: string, brandName: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${API_HOST}/auth/credit/thirdparty`;

    const tidInput = document.createElement('input');
    tidInput.type = 'hidden';
    tidInput.name = 'tid';
    tidInput.value = tid;
    form.appendChild(tidInput);

    const brandNameInput = document.createElement('input');
    brandNameInput.type = 'hidden';
    brandNameInput.name = 'brandName';
    brandNameInput.value = brandName;
    form.appendChild(brandNameInput);

    document.body.appendChild(form);
    form.submit();
  };

  const handleLoadPaymentData = async (
    paymentData: google.payments.api.PaymentData,
  ) => {
    setIsApproveProcessing(true);

    /**
     * 3DS 인증 form submit 으로 페이지가 이동하는 동안
     * 로더가 꺼지지 않도록 하기 위한 플래그
     */
    let isRedirecting = false;

    try {
      const { data: result } = await googlePayApprove({
        tid: paramTid,
        token: paymentData,
      });

      // 승인팀 요청 DEV 반영 3DS 인증분기 대체
      if (result.allowedAuthMethods === 'PAN_ONLY') {
        isRedirecting = true;
        submitThirdPartyAuthForm(result.tid, result.brandName);
        return;
      }

      // needAuth가 N인 경우 기존 흐름대로 결과 페이지로 이동
      navigate(`/ui/googlepay/result?tid=${paramTid}&appScheme=${appScheme}`);
    } catch (err) {
      console.error('Google Pay 승인 실패:', err);
      navigate(`/ui/googlepay/result?tid=${paramTid}&appScheme=${appScheme}`);
    } finally {
      if (!isRedirecting) {
        setIsApproveProcessing(false);
      }
    }
  };

  const googlepaySDKRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: 'CARD' as any,
        parameters: {
          allowedAuthMethods,
          allowedCardNetworks,
          billingAddressRequired: false,
          blockedIssuerCountryCodes: ['IN', 'KR'],
        },
        tokenizationSpecification: {
          // type: 'DIRECT' as any,
          type: 'PAYMENT_GATEWAY' as any,
          parameters: {
            // protocolVersion: paramProtocolVersion ?? 'ECv2',
            // publicKey: paramPublicKey ?? '',
            gateway: 'payverseglobal',
            gatewayMerchantId: 'eromnet',
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: paramMerchantId ?? '',
      merchantName: paramMerchantName ?? '',
    },
    transactionInfo: {
      totalPriceStatus: paramTotalPriceStatus,
      totalPriceLabel: paramTotalPriceLabel,
      totalPrice: paramAmount ?? '0',
      currencyCode: paramCurrency ?? '',
      countryCode: paramCountry ?? '',
    },
  };

  const env = (process.env.REACT_APP_ENV ?? '').toLowerCase();

  const googlePayEnvironment: google.payments.api.Environment = [
    'production',
    'stage',
    'staging',
    'sandbox',
  ].includes(env)
    ? 'PRODUCTION'
    : 'TEST';

  useEffect(() => {
    const urlParams = new URLSearchParams(searchParams);
    const scheme = urlParams.get('appScheme');

    if (scheme && scheme !== 'undefined') {
      setAppScheme(scheme);
    }
  }, [searchParams]);

  return (
    <Outer>
      <Header>
        <PayverseHeader />
        <PriceBox>
          <span>{t('pay_amount')}</span>
          <span>
            {Number(paramAmount).toLocaleString('en-US')} {paramCurrency}
          </span>
        </PriceBox>
      </Header>
      <Container>
        <CardBox>
          <img
            src="/puzzle/image/common/schemeList/GO/GOOGLEPAY-GLOBAL.png"
            alt="googlepay-global"
            width={50}
          />
        </CardBox>
        <h3>{t('descriptor')}</h3>
        <InfoBox>{paramDescriptor}</InfoBox>
      </Container>
      <Footer>
        <span>{t('agree_purchase')}</span>
        <GooglePayButton
          environment={googlePayEnvironment}
          buttonType="short"
          buttonColor="black"
          buttonSizeMode="fill"
          style={{ width: '100%', height: '60px' }}
          paymentRequest={googlepaySDKRequest}
          onLoadPaymentData={handleLoadPaymentData}
          onError={err => {
            console.error('Google Pay 오류:', err);
            navigate('/error');
          }}
          onCancel={() => {
            // 사용자가 직접 닫은 경우 — 별도 처리 불필요
          }}
        />
      </Footer>
    </Outer>
  );
};

export default GooglePayScreen;

const Outer = styled.div`
  width: 100%;
  height: 100dvh;
  box-sizing: border-box;
  position: relative;
`;

const Header = styled.header`
  width: 100%;
  box-shadow: 0 3px 3px 0 #cfcfcf40;
  box-sizing: border-box;
`;

const PriceBox = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  span {
    font-size: 18px;
    font-weight: 600;
  }
  span:first-child {
    color: black;
  }
  span:nth-child(2) {
    color: #3c62dd;
  }
`;

const Container = styled.div`
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  h3 {
    font-size: 16px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #212121;
  }
`;

const CardBox = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 6px;
  border: 2px solid #f5f5f5;
  padding-left: 22px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const InfoBox = styled.div`
  width: 100%;
  height: 50px;
  border-radius: 6px;
  border: 2px solid #f5f5f5;
  display: flex;
  align-items: center;
  padding-left: 22px;
  box-sizing: border-box;
  background-color: #eeeeee;
  color: #bdbdbd;
  margin-bottom: 16px;
`;

const Footer = styled.div`
  box-sizing: border-box;
  padding: 20px;
  position: absolute;
  z-index: 10;
  left: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  span {
    color: #757575;
    font-size: 16px;
  }
`;
